import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion as MOTION } from "framer-motion";
import axios from "../../utils/axios";
import Sidebar from "./sidebar";
import resolveImageUrl from "../../utils/imageUtils";
import { Toaster, toast } from "react-hot-toast";
import {
  trackAction,
  collectFeatureChanges,
  collectInspectorText,
  buildClickDetails,
} from "../../utils/clickTracking";
import { getPropertyUpdateErrorMessage } from "../../utils/propertyErrors";

const appendCacheBuster = (url, buster) => {
  if (!url || !buster) return url;
  return url.includes("?") ? `${url}&t=${buster}` : `${url}?t=${buster}`;
};
import { Layers, Navigation, Ruler, Home } from "lucide-react";

// Helper functions for area categories
const toFiniteNumber = (val) => {
  if (val === null || val === undefined) return null;
  if (typeof val === "number") return Number.isFinite(val) ? val : null;
  const n = Number(String(val).replaceAll(",", "").trim());
  return Number.isFinite(n) ? n : null;
};

const formatTwoDecimals = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value).trim();
  if (!str) return "";
  const lowered = str.toLowerCase();
  if (lowered === "null" || lowered === "undefined") return "";
  const n = toFiniteNumber(str);
  if (n === null) return str;
  return String(Math.round(n));
};

const inferLandAreaSqYards = (raw) => {
  const n = toFiniteNumber(raw);
  if (n === null || n < 0) return null;
  const rounded = Math.round(n);
  const isEffectivelyInteger = Math.abs(n - rounded) < 1e-6;
  if (n > 108000) return n / 9;
  if (n > 3000 && isEffectivelyInteger && rounded % 9 === 0) return n / 9;
  return n;
};

const inferCoveredAreaSqFt = (raw) => {
  const n = toFiniteNumber(raw);
  if (n === null || n < 0) return null;
  if (n <= 300) return n * 9;
  return n;
};

const GET_LAND_AREA_CATEGORY = (raw) => {
  const yards = inferLandAreaSqYards(raw);
  if (yards === null) return null;
  const cls =
    yards <= 31 ? 1 : yards <= 91 ? 2 : yards <= 175 ? 3 : yards <= 300 ? 4 : 5;
  return { cls };
};

const GET_COVERED_AREA_CATEGORY = (raw) => {
  const sqft = inferCoveredAreaSqFt(raw);
  if (sqft === null) return null;
  const cls =
    sqft <= 400 ? 1 : sqft <= 977 ? 2 : sqft <= 1710 ? 3 : sqft <= 2920 ? 4 : 5;
  return { cls };
};

const hasUsableValue = (value) => {
  if (value === null || value === undefined) return false;
  const str = String(value).trim().toLowerCase();
  return str !== "" && str !== "null" && str !== "undefined";
};

const pickFirstValue = (obj, keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (hasUsableValue(value)) return value;
  }
  return "";
};

const getMLDisplayValue = (property, field) => {
  let realValue = "";

  switch (field) {
    case 'floor':
      realValue = pickFirstValue(property, ["floors_ml", "floor_ml", "floors_pred", "floor_pred"]);
      break;
    case 'landuse':
      realValue = pickFirstValue(property, ["landuse_ml", "land_use_ml", "land_use_pred", "landuse_pred"]);
      break;
    case 'landarea':
      {
        const ml = pickFirstValue(property, ["landarea_ml", "land_area_ml"]);
        const bucket = pickFirstValue(property, ["landarea_combined_pred"]);
        const n = Number(String(ml ?? "").replaceAll(",", "").trim());
        const mlIsZeroish = Number.isFinite(n) && n <= 0;
        realValue = hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
      }
      break;
    case 'coveredarea':
      {
        const ml = pickFirstValue(property, ["coveredarea_ml", "covered_area_ml"]);
        const bucket = pickFirstValue(property, ["coveredarea_combined_pred"]);
        const n = Number(String(ml ?? "").replaceAll(",", "").trim());
        const mlIsZeroish = Number.isFinite(n) && n <= 0;
        realValue = hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
      }
      break;
  }

  if (hasUsableValue(realValue)) {
    if (field === "landarea" || field === "coveredarea") {
      // Check if it's a range string first
      const strVal = String(realValue);
      const isRange = strVal.toLowerCase().includes("greater") ||
        strVal.toLowerCase().includes("less") ||
        strVal.toLowerCase().includes("between");
      if (isRange) {
        return realValue;
      }
      const formatted = formatTwoDecimals(realValue);
      return formatted || "N/A";
    }
    const num = Number(realValue);
    if (Number.isFinite(num) && !isNaN(num)) {
      return Number.isInteger(num) ? num : Math.round(num);
    }
    return realValue;
  }
  return 'N/A';
};

const parseRange = (rangeStr) => {
  if (!rangeStr) return null;
  const str = String(rangeStr).toLowerCase().trim();

  // Remove any units (sq yards, sq yds, sq ft, sq.yds, sq.ft, etc.)
  const cleanStr = str.replace(/sq\.?\s*(yards?|yds?|ft|feet?)|,/g, '').trim();

  // Check for "greater than X" or "more than X"
  let match = cleanStr.match(/(?:greater|more)\s+than\s+(\d+(?:\.\d+)?)/);
  if (match) {
    return { type: 'greater', value: Number(match[1]) };
  }

  // Check for "less than X" or "under X"
  match = cleanStr.match(/(?:less|under)\s+than\s+(\d+(?:\.\d+)?)/);
  if (match) {
    return { type: 'less', value: Number(match[1]) };
  }

  // Check for "between X and Y"
  match = cleanStr.match(/between\s+(\d+(?:\.\d+)?)\s+and\s+(\d+(?:\.\d+)?)/);
  if (match) {
    return {
      type: 'between',
      min: Number(match[1]),
      max: Number(match[2])
    };
  }

  // Try to parse as a single number
  const num = Number(cleanStr);
  if (Number.isFinite(num)) {
    return { type: 'exact', value: num };
  }

  return null;
};

const areValuesDifferent = (adminVal, mlVal, field) => {
  const normalize = (val) => {
    if (val === null || val === undefined) return null;
    const raw = String(val).trim();
    if (!raw) return null;
    const lowered = raw.toLowerCase();
    if (lowered === "n/a" || lowered === "null" || lowered === "undefined") return null;
    // Try to extract number for area fields
    if (field === "landarea" || field === "coveredarea") {
      const numStr = raw.replace(/[^0-9.]/g, '');
      const numeric = Number(numStr);
      if (Number.isFinite(numeric)) return numeric;
    }
    const numeric = Number(raw.replaceAll(",", ""));
    if (Number.isFinite(numeric)) return numeric;
    return lowered;
  };

  // For landarea and coveredarea, check if admin is within ML range
  if ((field === "landarea" || field === "land_area" || field === "coveredarea" || field === "covered_area") && mlVal) {
    const range = parseRange(mlVal);
    const a = normalize(adminVal);
    if (range && typeof a === "number") {
      switch (range.type) {
        case "greater":
          return a <= range.value; // highlight if admin is NOT greater
        case "less":
          return a >= range.value; // highlight if admin is NOT less
        case "between":
          return a < range.min || a > range.max; // highlight if outside range
        case "exact":
          return Math.abs(a - range.value) > 1e-9; // highlight if different
      }
    }
  }

  // For all other fields, use original comparison
  const a = normalize(adminVal);
  const m = normalize(mlVal);
  if (a === null || m === null) return false;
  if (typeof a === "number" && typeof m === "number") return Math.abs(a - m) > 1e-9;
  return a !== m;
};

const Property = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [reassessFilter, setReassessFilter] = useState("all"); // all | reassessed | not_reassessed
  const [selectedProperty, setSelectedProperty] = useState(null); // For modal
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState("");
  const [showFullImage, setShowFullImage] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [savingImageRotation, setSavingImageRotation] = useState(false);
  const [autoOpenedPin, setAutoOpenedPin] = useState("");
  const [highlightPin, setHighlightPin] = useState("");

  const normalizePin = (pin) => String(pin || "").trim().toLowerCase();
  const dedupeByPin = (list) => {
    const seen = new Set();
    return (list || []).filter((p) => {
      const key = normalizePin(p?.pin);
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };
  const highlightPropertyCard = (pin) => {
    const normalized = normalizePin(pin);
    if (!normalized) return;
    setHighlightPin(normalized);
    setTimeout(() => {
      const el = document.querySelector(`[data-pin="${normalized}"]`);
      if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const closeDetailModal = () => {
    const pinToHighlight = selectedProperty?.pin;
    setSelectedProperty(null);
    setRotation(0);
    setTimeline([]);
    setTimelineLoading(false);
    setTimelineError("");
    highlightPropertyCard(pinToHighlight);
    navigate({ search: "" }, { replace: true });
  };

  // Reassessment states
  const [_showReassessModal, setShowReassessModal] = useState(false);
  const [_showNotReassessModal, setShowNotReassessModal] = useState(false);
  const [_showRefModal, setShowRefModal] = useState(false);
  const [features, setFeatures] = useState([]);
  const [overallExplanation, _setOverallExplanation] = useState("");
  const [rowExplanations, setRowExplanations] = useState({
    floor: "",
    landuse: "",
    landarea: "",
    coveredarea: ""
  });
  const [_referenceNumber, _setReferenceNumber] = useState("");
  const [readyToSubmit, setReadyToSubmit] = useState(false);
  const [_submitting, setSubmitting] = useState(false);
  const [notReassessComment, _setNotReassessComment] = useState("");
  const [imageLoadErrors, setImageLoadErrors] = useState({});

  // Support multiple possible image fields coming from API
  const getPropertyImage = (p) => {
    if (!p) return null;
    if (p.image) return p.image;
    if (p.image_url) return p.image_url;
    if (p.imageUrl) return p.imageUrl;
    if (p.image_path) return p.image_path;
    if (Array.isArray(p.images) && p.images.length > 0) return p.images[0];
    if (Array.isArray(p.property_images) && p.property_images.length > 0) {
      const first = p.property_images[0];
      return first?.image || first?.url || first?.path || null;
    }
    return null;
  };

  const handleRotate = (e) => {
    e.stopPropagation();
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleSaveRotation = async (e) => {
    e.stopPropagation();
    if (!selectedProperty?.pin || rotation === 0 || savingImageRotation) return;

    const toastId = toast.loading("Saving rotated image...");
    setSavingImageRotation(true);

    try {
      const totalRotations = Math.abs(rotation / 90);
      for (let i = 0; i < totalRotations; i++) {
        await axios.post(`/properties/rotate-image-id/${selectedProperty.id}`, {
          pin: selectedProperty.pin,
          rotateBy: 90,
          forcePortrait: false
        });
      }

      const bust = Date.now();
      setProperties((prev) =>
        prev.map((p) => (p.pin === selectedProperty.pin ? { ...p, _imgBust: bust } : p))
      );
      setSelectedProperty((prev) =>
        prev?.pin === selectedProperty.pin ? { ...prev, _imgBust: bust } : prev
      );
      setRotation(0);
      toast.success("Image saved successfully!", { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to save image", {
        id: toastId,
      });
    } finally {
      setSavingImageRotation(false);
    }
  };
  const itemsPerPage = 12;

  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.id;
  const searchParams = new URLSearchParams(location.search);
  const targetPinFromQuery = searchParams.get("pin")?.trim() || "";

  const toTime = (val) => {
    const t = val ? new Date(val).getTime() : 0;
    return Number.isFinite(t) ? t : 0;
  };

  const sortResolvedList = (list) => {
    return [...(list || [])].sort((a, b) => {
      const at =
        toTime(a.last_action_at) ||
        toTime(a.reassessment_date) ||
        toTime(a.inspector_explanation_created_at) ||
        toTime(a.supervisor_assigned_at) ||
        (Number(a.id) || 0);
      const bt =
        toTime(b.last_action_at) ||
        toTime(b.reassessment_date) ||
        toTime(b.inspector_explanation_created_at) ||
        toTime(b.supervisor_assigned_at) ||
        (Number(b.id) || 0);
      if (bt !== at) return bt - at;
      return (Number(b.id) || 0) - (Number(a.id) || 0);
    });
  };

  // Reassessment helper functions
  const BUILD_FEATURES = (p) => [
    { key: "floor", label: "Floor", before: p.floor_admin, after: pickFirstValue(p, ["floors_pred", "floor_pred", "floors_ml", "floor_ml"]) },
    { key: "land_use", label: "Land Use", before: p.landuse_admin, after: pickFirstValue(p, ["land_use_pred", "landuse_pred", "landuse_ml", "land_use"]) },
    { key: "land_area", label: "Land Area", before: p.landarea_admin, after: pickFirstValue(p, ["landarea_combined_pred", "land_area_ml", "landarea_ml", "land_area"]) },
    { key: "covered_area", label: "Covered Area", before: p.coveredarea_admin, after: pickFirstValue(p, ["coveredarea_combined_pred", "covered_area_ml", "coveredarea_ml", "covered_area"]) },
    { key: "pt", label: "PT-5 Serial", before: "", after: pickFirstValue(p, ["refno", "pt5", "pt_5", "pt"]) },
  ];

  const UPDATE_FEATURE = (i, field, value) => {
    const updated = [...features];
    const f = updated[i];
    let validatedValue = value;
    if (field === "after") {
      switch (f.key) {
        case "floor":
        case "land_area":
        case "covered_area":
          if (/[^0-9.]/.test(value)) {
            toast.error("Please enter only numbers for this field!");
            validatedValue = value.replace(/[^0-9.]/g, "");
          }
          if ((validatedValue.match(/\./g) || []).length > 1) {
            validatedValue = validatedValue.slice(0, -1);
          }
          break;
        case "land_use":
          if (/[0-9]/.test(value)) {
            toast.error("Please enter only letters for Land Use!");
            validatedValue = value.replace(/[0-9]/g, "");
          }
          break;
      }
    }
    updated[i][field] = validatedValue;
    setFeatures(updated);
  };

  const UPDATE_ROW_EXPLANATION = (key, value) => {
    setRowExplanations(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSubmitReassess = async () => {
    if (!selectedProperty) return;
    try {
      setSubmitting(true);

      if (overallExplanation && userId) {
        await axios.post("/inspector/save-explanation", {
          property_id: selectedProperty.id,
          user_id: userId,
          overall_explanation: overallExplanation,
          row_explanations: rowExplanations
        });
      }

      const fd = new FormData();
      fd.append("pin", selectedProperty.pin);
      fd.append("userId", userId || "");
      fd.append("reassess", "1");

      fd.append("circle", selectedProperty.circle || "");
      fd.append("occupationstatus", selectedProperty.occupationstatus || "");
      fd.append("location", selectedProperty.location || "");
      fd.append("floor_admin", selectedProperty.floor_admin || "");
      fd.append("landuse_admin", selectedProperty.landuse_admin || "");
      fd.append("landarea_admin", selectedProperty.landarea_admin || "");
      fd.append("coveredarea_admin", selectedProperty.coveredarea_admin || "");
      fd.append("admin_garv", selectedProperty.admin_garv || "");

      features.forEach(f => {
        const val = f.after?.toString().trim();
        if (!val) return;
        switch (f.key) {
          case "floor": fd.append("floors_pred", val); break;
          case "land_use": fd.append("land_use_pred", val); break;
          case "land_area": fd.append("landarea_combined_pred", val); break;
          case "covered_area": fd.append("coveredarea_combined_pred", val); break;
          case "pt": fd.append("refno", val); break;
        }
      });

      await axios.put(`/properties/${selectedProperty.pin}`, fd);

      const featureChanges = collectFeatureChanges(features);
      await trackAction(
        "reassess",
        selectedProperty.pin,
        buildClickDetails({
          featureChanges: featureChanges.length ? featureChanges : ["No feature changes"],
          inspectorText: collectInspectorText({ overallExplanation, rowExplanations, features }),
        })
      );

      toast.success("Reassessment updated!");

      setShowReassessModal(false);
      setShowRefModal(false);
      setSelectedProperty(null);
      setReadyToSubmit(false);

      // const res = await axios.get(`/inspector/resolved-not-filtered/${userId}`);
      // setProperties(dedupeByPin(sortResolvedList(res.data)));
      const res = await axios.get(`/inspector/resolved-not-filtered/${userId}`);
      const fetchBust1 = Date.now();
      setProperties(dedupeByPin(sortResolvedList(res.data)).map((p) => ({ ...p, _imgBust: p._imgBust ?? fetchBust1 })));
    } catch (err) {
      console.error(err);
      toast.error(getPropertyUpdateErrorMessage(err, "Failed to update reassessment"));
    } finally {
      setSubmitting(false);
    }
  };

  const HANDLE_NOT_REASSESS = async () => {
    if (!selectedProperty) return;
    try {
      setSubmitting(true);
      const trimmed = String(notReassessComment ?? "").trim();
      const fd = new FormData();
      fd.append("pin", selectedProperty.pin);
      fd.append("userId", userId || "");
      fd.append("reassess", "0");
      fd.append("comment", trimmed);

      ["circle", "occupationstatus", "location", "floor_admin", "landuse_admin", "landarea_admin", "coveredarea_admin", "admin_garv"].forEach(field => {
        fd.append(field, selectedProperty[field] || "");
      });

      if (trimmed && userId) {
        try {
          await axios.post("/inspector/save-explanation", {
            property_id: selectedProperty.id,
            user_id: userId,
            overall_explanation: trimmed,
            row_explanations: {
              floor: "",
              landuse: "",
              landarea: "",
              coveredarea: "",
            },
          });
        } catch (err) {
          console.error("Failed to save explanation:", err);
        }
      }

      await axios.put(`/properties/${selectedProperty.pin}`, fd);

      await trackAction(
        "do_not_reassess",
        selectedProperty.pin,
        buildClickDetails({
          inspectorText: trimmed
            ? `Reason for not reassessing: ${trimmed}`
            : "No reason provided",
        }),
        {
          comment_text: trimmed
            ? `Reason for not reassessing: ${trimmed}`
            : "No reason provided",
        }
      );

      toast.success("Updated to 'Do Not Reassess'");

      setShowNotReassessModal(false);

      // const res = await axios.get(`/inspector/resolved-not-filtered/${userId}`);
      // setProperties(dedupeByPin(sortResolvedList(res.data)));
      const res = await axios.get(`/inspector/resolved-not-filtered/${userId}`);
      const fetchBust2 = Date.now();
      setProperties(dedupeByPin(sortResolvedList(res.data)).map((p) => ({ ...p, _imgBust: p._imgBust ?? fetchBust2 })));
    } catch (err) {
      console.error(err);
      toast.error("Failed to update status");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (readyToSubmit && selectedProperty) handleSubmitReassess();
  }, [readyToSubmit]);

  useEffect(() => {
    setCurrentPage(1);
  }, [reassessFilter]);

  const IS_REASSESS_VALID =
    features.filter(f => f.key !== "pt").some(f => f.after?.toString().trim() !== "");

  useEffect(() => {
    // axios
    //   .get(`/inspector/resolved-not-filtered/${userId}`)
    //   .then((res) => setProperties(dedupeByPin(sortResolvedList(res.data))))
    //   .catch((err) => console.error("Failed to fetch properties:", err));
    axios
      .get(`/inspector/resolved-not-filtered/${userId}`)
      .then((res) => {
        const fetchBust = Date.now();
        setProperties(dedupeByPin(sortResolvedList(res.data)).map((p) => ({ ...p, _imgBust: p._imgBust ?? fetchBust })));
      })
      .catch((err) => console.error("Failed to fetch properties:", err));
  }, [userId]);

  useEffect(() => {
    const pin = selectedProperty?.pin;
    if (!pin) {
      setTimeline([]);
      setTimelineLoading(false);
      setTimelineError("");
      return;
    }

    const fetchTimeline = async () => {
      try {
        setTimelineLoading(true);
        setTimelineError("");
        const res = await axios.get(`/properties/timeline-id/${selectedProperty.id}`);
        setTimeline(res.data?.timeline || []);
      } catch (err) {
        console.error("Failed to fetch timeline:", err);
        setTimeline([]);
        setTimelineError("Failed to load previous comments.");
      } finally {
        setTimelineLoading(false);
      }
    };

    fetchTimeline();
  }, [selectedProperty?.pin]);

  useEffect(() => {
    if (!targetPinFromQuery) return;
    if (!userId) return;
    if (autoOpenedPin && autoOpenedPin === targetPinFromQuery) return;

    const normalizedTarget = targetPinFromQuery.toLowerCase();
    const matched = properties.find(
      (p) => String(p.pin || "").trim().toLowerCase() === normalizedTarget
    );

    if (matched) {
      setSelectedProperty(matched);
      setAutoOpenedPin(targetPinFromQuery);
      return;
    }

    axios
      .get(`/inspector/property/${encodeURIComponent(targetPinFromQuery)}`)
      .then((res) => {
        const data = res.data;
        const workflow = String(data?.status || "").trim().toLowerCase();
        if (workflow === "resolved") {
          setSelectedProperty(data);
          setAutoOpenedPin(targetPinFromQuery);
        }
      })
      .catch(() => { });
  }, [targetPinFromQuery, userId, autoOpenedPin, properties]);

  const isReassessYes = (value) => {
    if (value == 1) return true;
    const str = String(value ?? "").trim().toLowerCase();
    return str === "1" || str === "true" || str === "yes";
  };

  const reassessedCount = properties.filter((p) => isReassessYes(p.reassess)).length;
  const notReassessedCount = properties.length - reassessedCount;

  const filteredProperties = properties.filter((p) => {
    const isYes = isReassessYes(p.reassess);
    if (reassessFilter === "reassessed") return isYes;
    if (reassessFilter === "not_reassessed") return !isYes;
    return true;
  });

  const totalPages = Math.ceil(filteredProperties.length / itemsPerPage);
  const paginatedProperties = filteredProperties.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    if (totalPages <= 0) {
      if (currentPage !== 1) setCurrentPage(1);
      return;
    }
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const hasValidImage = (prop) => {
    const img = getPropertyImage(prop);
    if (!img) return false;
    return !imageLoadErrors[prop.pin];
  };

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      <Sidebar />

      <main className="flex-1 p-6 overflow-y-auto">
        <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-teal-700">
              Resolved Properties
            </h1>
            <p className="text-gray-600 mt-3 text-base italic border-l-4 border-blue-400 pl-4 bg-blue-50 py-2 rounded-r">
              Hello! These properties have been accepted by your supervisor and have been marked as resolved.
            </p>
          </div>
          <div className="text-xs sm:text-sm bg-gray-100 text-gray-600 px-3 py-1 rounded-full border border-gray-200">
            Showing <span className="font-semibold">{paginatedProperties.length}</span> of{" "}
            <span className="font-semibold">{filteredProperties.length}</span> properties
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {/* <button
            type="button"
            onClick={() => setReassessFilter("all")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              reassessFilter === "all"
                ? "bg-teal-700 text-white"
                : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
            }`}
            data-click-id="resolved_all"
          >
            All ({properties.length})
          </button> */}
          {/* <button
            type="button"
            onClick={() => setReassessFilter("reassessed")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              reassessFilter === "reassessed"
                ? "bg-teal-700 text-white"
                : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
            }`}
            data-click-id="resolved_reassessed"
          >
            Reassessed ({reassessedCount})
          </button> */}
          {/* <button
            type="button"
            onClick={() => setReassessFilter("not_reassessed")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              reassessFilter === "not_reassessed"
                ? "bg-teal-700 text-white"
                : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
            }`}
            data-click-id="resolved_not_reassessed"
          >
            Not Reassessed ({notReassessedCount})
          </button> */}
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {paginatedProperties.length === 0 ? (
            <p className="text-gray-500">
              No resolved properties found.
            </p>
          ) : (
            paginatedProperties.map((prop, index) => (
              <div
                key={prop.id ?? prop.pin ?? index}
                onClick={() => setSelectedProperty(prop)} // CLICK OPENS MODAL
                data-pin={normalizePin(prop.pin)}
                className={`bg-white shadow hover:shadow-lg overflow-hidden group relative cursor-pointer transition-all rounded-xl ${normalizePin(prop.pin) && normalizePin(prop.pin) === highlightPin
                  ? "border-2 border-sky-500"
                  : "border border-gray-200"
                  }`}
              >
                {normalizePin(prop.pin) && normalizePin(prop.pin) === highlightPin && (
                  <div className="absolute top-2 left-2 z-20 w-7 h-7 rounded-full bg-sky-600 text-white flex items-center justify-center text-sm font-bold shadow">
                    ✓
                  </div>
                )}
                <div
                  className="h-48 bg-gray-100 overflow-hidden cursor-pointer flex items-center justify-center"
                  onClick={() => setSelectedProperty(prop)}
                >
                  {hasValidImage(prop) ? (
                    <div className="h-full w-full transition-transform duration-300 group-hover:scale-105">
                      <img
                        src={appendCacheBuster(resolveImageUrl(getPropertyImage(prop)), prop._imgBust)}
                        alt="Property"
                        className="h-full w-full object-cover"
                        onError={() =>
                          setImageLoadErrors((prev) => ({ ...prev, [prop.pin]: true }))
                        }
                      />
                    </div>
                  ) : (
                    <div className="h-full w-full flex items-center justify-center bg-gray-50">
                      <svg
                        className="w-12 h-12 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  )}
                </div>

                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-bold text-lg text-gray-900 truncate pr-2">
                      {prop.pin}
                    </h3>
                  </div>

                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      </svg>
                      <span>Circle: {prop.circle || "N/A"}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-3">
                    {/* {prop.status && (
                      <span className="inline-block text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                        {prop.status}
                      </span>
                    )} */}
                    {/* {prop.landarea_admin && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Land: {formatTwoDecimals(prop.landarea_admin) || prop.landarea_admin} sq.yards
                      </span>
                    )} */}
                    {/* {prop.coveredarea_admin && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Covered: {formatTwoDecimals(prop.coveredarea_admin) || prop.coveredarea_admin} sq.ft
                      </span>
                    )} */}
                  </div>

                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProperty(prop);
                      }}
                      className="w-full py-2 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors flex items-center justify-center gap-1"
                      data-click-id="resolved_all"
                      data-property-id={prop.pin}
                    >
                      View Details
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center mt-8 space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              className="px-3 py-1 bg-teal-600 text-white rounded disabled:opacity-50"
              disabled={currentPage === 1}
              data-click-id="resolved_page_previous"
            >
              Prev
            </button>

            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 rounded ${currentPage === i + 1
                  ? "bg-teal-700 text-white"
                  : "bg-gray-200 text-gray-800"
                  }`}
              >
                {i + 1}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              className="px-3 py-1 bg-teal-600 text-white rounded disabled:opacity-50"
              disabled={currentPage === totalPages}
              data-click-id="resolved_page_next"
            >
              Next
            </button>
          </div>
        )}
      </main>

      {/* DETAIL MODAL - Full width and fit to screen */}
      <AnimatePresence>
        {selectedProperty && (
          <MOTION.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50"
          >
            <MOTION.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white w-full h-full shadow-2xl p-6 relative overflow-y-auto"
            >
              {/* Close button */}
              <button
                onClick={closeDetailModal}
                className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors z-10 flex items-center justify-center w-10 h-10"
                data-click-id="reviewed_close_details"
                data-property-id={selectedProperty?.pin}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Header Info like Screenshot */}
              <div className="mb-6 pr-14">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-gray-500 text-sm">Circle</p>
                    <p className="text-lg font-bold text-gray-800 uppercase">{selectedProperty.circle || "N/A"}</p>
                  </div>
                </div>
                <div className="flex justify-between items-center text-sm text-gray-500">
                  <div>
                    <p>
                      PIN: <span className="font-semibold text-gray-700">{selectedProperty.pin}</span>
                    </p>
                    <p className="mt-1">
                      PT-5 Serial Number :{" "}
                      <span className="font-semibold text-gray-700">
                        {pickFirstValue(selectedProperty, ["refno", "pt5", "pt_5", "pt"]) || "N/A"}
                      </span>
                    </p>
                    <p className="mt-1">
                      PT-6 Issue Date :{" "}
                      <span className="font-semibold text-gray-700">
                        {selectedProperty.pt6_issue_date
                          ? new Date(selectedProperty.pt6_issue_date).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </p>
                  </div>
                  <p>&nbsp;</p>
                </div>
                {/* Reassessed Status moved here - below PIN with bigger font */}
                <div className="mt-3">
                  <p className="text-gray-500 text-md mb-2">Reassessment Status</p>
                  <span
                    className={`inline-flex items-center px-4 py-2 rounded-full text-base font-bold ${selectedProperty.reassess == 1
                      ? "bg-gray-100 text-gray-800"
                      : "bg-gray-100 text-gray-800"
                      }`}
                  >
                    {selectedProperty.reassess == 1 ? "✓ Reassessed" : "✗ Not Reassessed"}
                  </span>
                </div>
              </div>

              {/* IMAGE and Info Section - Side by side layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Image Section */}
                <div className="relative group bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center cursor-pointer h-80">
                  {getPropertyImage(selectedProperty) && !imageLoadErrors[selectedProperty.pin] ? (
                    <>
                      <div
                        className="w-full h-full transition-transform duration-300 ease-in-out flex items-center justify-center"
                        style={{ transform: `rotate(${rotation}deg)` }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowFullImage(true);
                        }}
                      >
                        <img
                          src={appendCacheBuster(resolveImageUrl(getPropertyImage(selectedProperty)), selectedProperty._imgBust)}
                          className="max-w-full max-h-full object-contain hover:opacity-90 transition-opacity"
                          alt="Property"
                          onError={() =>
                            setImageLoadErrors((prev) => ({ ...prev, [selectedProperty.pin]: true }))
                          }
                        />
                      </div>
                      <div
                        className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none"
                      >
                        <span className="text-white bg-black/50 px-3 py-1 rounded-full text-sm font-medium">Click to Enlarge</span>
                      </div>
                      <div className="absolute bottom-4 right-4 flex gap-2 z-10">
                        <button
                          onClick={handleRotate}
                          className="p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-all shadow-lg"
                          title="Rotate Image"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </button>
                        {rotation !== 0 && (
                          <button
                            onClick={handleSaveRotation}
                            disabled={savingImageRotation}
                            className="p-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-full text-white transition-all shadow-lg flex items-center gap-2"
                            title="Save Rotation"
                          >
                            {savingImageRotation ? (
                              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <>
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-60 bg-gray-200 flex items-center justify-center rounded-lg text-gray-500">
                      No Image
                    </div>
                  )}
                </div>

                {/* Property Information Table */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">Property Information</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border rounded-lg overflow-hidden text-sm">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="p-2 text-left font-medium">Feature</th>
                          <th className="p-2 text-left font-medium">Admin</th>
                          {/* Computer Vision column — kept for future use
                          <th className="p-2 text-left font-medium">Computer Vision</th>
                          */}
                          <th className="p-2 text-left font-medium">Reassessment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* <tr
                          className={`border-b ${
                            areValuesDifferent(
                              selectedProperty.floor_admin,
                              getMLDisplayValue(selectedProperty, "floor"),
                              "floor"
                            )
                              ? "bg-red-100"
                              : ""
                          }`}
                        > */}
                        <tr className="border-b">
                          <td className="p-2 font-medium">Floor</td>
                          <td className="p-2">
                            {hasUsableValue(selectedProperty.floor_admin)
                              ? selectedProperty.floor_admin
                              : "N/A"}
                          </td>
                          {/* <td className="p-2">{getMLDisplayValue(selectedProperty, "floor")}</td> */}
                          <td className="p-2">
                            {selectedProperty.reassess == 1 ? (selectedProperty.floors_pred || "N/A") : "N/A"}
                          </td>
                        </tr>

                        {/* <tr
                          className={`border-b ${
                            areValuesDifferent(
                              selectedProperty.landuse_admin,
                              getMLDisplayValue(selectedProperty, "landuse"),
                              "landuse"
                            )
                              ? "bg-red-100"
                              : ""
                          }`}
                        > */}
                        <tr className="border-b">
                          <td className="p-2 font-medium">Land Use</td>
                          <td className="p-2">
                            {hasUsableValue(selectedProperty.landuse_admin)
                              ? selectedProperty.landuse_admin
                              : "N/A"}
                          </td>
                          {/* <td className="p-2">{getMLDisplayValue(selectedProperty, "landuse")}</td> */}
                          <td className="p-2">
                            {selectedProperty.reassess == 1 ? (selectedProperty.land_use_pred || "N/A") : "N/A"}
                          </td>
                        </tr>

                        {/* <tr
                          className={`border-b ${
                            areValuesDifferent(
                              selectedProperty.landarea_admin,
                              getMLDisplayValue(selectedProperty, "landarea"),
                              "landarea"
                            )
                              ? "bg-red-100"
                              : ""
                          }`}
                        > */}
                        <tr className="border-b">
                          <td className="p-2 font-medium">Land Area</td>
                          <td className="p-2">
                            {hasUsableValue(selectedProperty.landarea_admin)
                              ? `${formatTwoDecimals(selectedProperty.landarea_admin) || selectedProperty.landarea_admin} sq.yards`
                              : "N/A"}
                          </td>
                          {/* <td className="p-2">{getMLDisplayValue(selectedProperty, "landarea")}</td> */}
                          <td className="p-2">
                            {/* {selectedProperty.reassess == 1
                              ? formatTwoDecimals(selectedProperty.landarea_combined_pred) || "N/A"
                              : "N/A"} */}
                          </td>
                        </tr>

                        {/* <tr className={`border-b ${areValuesDifferent(selectedProperty.coveredarea_admin,getMLDisplayValue(selectedProperty, "coveredarea"),"coveredarea")? "bg-red-100": ""}`}> */}
                        <tr className="border-b">
                          <td className="p-2 font-medium">Covered Area</td>
                          <td className="p-2">
                            {hasUsableValue(selectedProperty.coveredarea_admin)
                              ? `${formatTwoDecimals(selectedProperty.coveredarea_admin) || selectedProperty.coveredarea_admin} sq.ft`
                              : "N/A"}
                          </td>
                          {/* <td className="p-2">{getMLDisplayValue(selectedProperty, "coveredarea")}</td> */}
                          <td className="p-2">
                            {/* {selectedProperty.reassess == 1
                              ? formatTwoDecimals(selectedProperty.coveredarea_combined_pred) || "N/A"
                              : "N/A"} */}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* {(selectedProperty.supervisor_reason || selectedProperty.reason) && (
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-xl p-4 mb-6 shadow-sm">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <h3 className="font-semibold text-gray-800">Supervisor Comment</h3>
                    </div>
                    {selectedProperty.supervisor_assigned_at && (
                      <div className="text-xs text-gray-700">
                        {new Date(selectedProperty.supervisor_assigned_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <p className="text-gray-700 text-sm whitespace-pre-wrap">
                    {selectedProperty.supervisor_reason || selectedProperty.reason}
                  </p>
                </div>
              )} */}

              {(() => {
                const agreementItem = (timeline || []).find(
                  (item) => item?.type === "algorithm_response"
                );
                if (!agreementItem) return null;
                const stage = agreementItem.stage || "";
                let agreementText = "";
                if (stage.includes("(Agreed)")) agreementText = "(Agreed)";
                else if (stage.includes("(Disagreed)")) agreementText = "(Disagreed)";
                else if (stage.includes("(Partially Agreed)")) agreementText = "(Partially Agreed)";
                const createdAt = agreementItem?.created_at
                  ? new Date(agreementItem.created_at).toLocaleString()
                  : "";
                return (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 shadow-sm">
                    <p className="text-blue-800 text-sm whitespace-pre-wrap">
                      Did inspector agree with Computer Vision Assessment?
                      <br />
                      {agreementText}
                    </p>
                  </div>
                );
              })()}

              {user?.username === "inspector9" && (
                (() => {
                  // Try to find any supervisor comment that is not auto-generated.
                  const isSupervisorItem = (item) =>
                    (item?.type === "supervisor" ||
                      String(item?.user_role || "").toLowerCase() === "supervisor") &&
                    item?.type !== "action";

                  const supervisorFlag = timeline.find(
                    (item) =>
                      isSupervisorItem(item) &&
                      item.text &&
                      item.text !== "Property reassessed by inspector" &&
                      item.text !== "Property accepted by supervisor"
                  ) || timeline.find(
                    (item) =>
                      isSupervisorItem(item) &&
                      item.stage &&
                      item.stage.includes("Flagged by Supervisor")
                  );

                  // Debug: uncomment to see what is found
                  // console.log("supervisorFlag found:", supervisorFlag);

                  if (!supervisorFlag) return null;

                  return (
                    <div className="mb-6 bg-white border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          Supervisor {supervisorFlag.user_name || "Supervisor"} Flagged this property
                        </h3>
                        {supervisorFlag.created_at && (
                          <div className="text-sm text-gray-500">
                            {new Date(supervisorFlag.created_at).toLocaleString()}
                          </div>
                        )}
                      </div>
                      <div className="text-sm text-gray-800 whitespace-pre-wrap">
                        {supervisorFlag.text || ""}
                      </div>
                    </div>
                  );
                })()
              )}

              {(selectedProperty.inspector_overall_explanation ||
                selectedProperty.inspector_floor_explanation ||
                selectedProperty.inspector_landuse_explanation ||
                selectedProperty.inspector_landarea_explanation ||
                selectedProperty.inspector_coveredarea_explanation) && (
                  <div className="mb-6 bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">Inspector Comments</h3>
                      {selectedProperty.inspector_explanation_created_at && (
                        <div className="text-sm text-gray-500">
                          {new Date(selectedProperty.inspector_explanation_created_at).toLocaleString()}
                        </div>
                      )}
                    </div>

                    {selectedProperty.inspector_overall_explanation && (
                      <div className="mb-3">
                        <div className="text-sm font-semibold text-gray-700">Overall</div>
                        <div className="text-sm text-gray-800 whitespace-pre-wrap">
                          {selectedProperty.inspector_overall_explanation}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {selectedProperty.inspector_floor_explanation && (
                        <div className="border border-gray-200 rounded-lg p-3">
                          <div className="text-sm font-semibold text-gray-700">Floor</div>
                          <div className="text-sm text-gray-800 whitespace-pre-wrap">
                            {selectedProperty.inspector_floor_explanation}
                          </div>
                        </div>
                      )}
                      {selectedProperty.inspector_landuse_explanation && (
                        <div className="border border-gray-200 rounded-lg p-3">
                          <div className="text-sm font-semibold text-gray-700">Land Use</div>
                          <div className="text-sm text-gray-800 whitespace-pre-wrap">
                            {selectedProperty.inspector_landuse_explanation}
                          </div>
                        </div>
                      )}
                      {selectedProperty.inspector_landarea_explanation && (
                        <div className="border border-gray-200 rounded-lg p-3">
                          <div className="text-sm font-semibold text-gray-700">Land Area</div>
                          <div className="text-sm text-gray-800 whitespace-pre-wrap">
                            {selectedProperty.inspector_landarea_explanation}
                          </div>
                        </div>
                      )}
                      {selectedProperty.inspector_coveredarea_explanation && (
                        <div className="border border-gray-200 rounded-lg p-3">
                          <div className="text-sm font-semibold text-gray-700">Covered Area</div>
                          <div className="text-sm text-gray-800 whitespace-pre-wrap">
                            {selectedProperty.inspector_coveredarea_explanation}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

              {user?.username === "inspector90" && (
                <div className="mb-6 bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">Comment Timeline</h3>
                    <div className="text-sm text-gray-500">Chat view</div>
                  </div>

                  {timelineLoading ? (
                    <div className="text-sm text-gray-600">Loading...</div>
                  ) : timelineError ? (
                    <div className="text-sm text-red-600">{timelineError}</div>
                  ) : timeline.length === 0 ? (
                    <div className="text-sm text-gray-600">No previous comments found for this property.</div>
                  ) : (
                    <div className="space-y-3">
                      {timeline
                        .filter((item) => {
                          // Skip "Flagged by Admin via Bulk Assignment" comments from Computer Vision
                          if (item.type === "algorithm_flag" &&
                            item.text === "Flagged by Admin via Bulk Assignment") {
                            return false;
                          }
                          // Skip "Property reassessed by inspector" supervisor comments
                          if (item.type === "supervisor" &&
                            item.text === "Property reassessed by inspector") {
                            return false;
                          }
                          return true;
                        })
                        .map((item) => {
                          const createdAt = item?.created_at ? new Date(item.created_at).toLocaleString() : "";
                          const role = String(item.user_role || "").toLowerCase();
                          const isSupervisor = item.type === "supervisor" || role === "supervisor";
                          const isInspector = item.type === "inspector_explanation" || role === "inspector";
                          const isAction = item.type === "action";
                          const isAlgorithmResponse = item.type === "algorithm_response";

                          const actionText = (() => {
                            if (!isAction) return "";
                            if (String(item.action || "").startsWith("FLAG_PROPERTY")) return "Flagged";
                            if (item.action === "ACCEPT_PROPERTY") return "Resolved";
                            if (item.action === "UNFLAG_PROPERTY") return "Unflagged";
                            return String(item.action || "Action");
                          })();

                          const rows = item?.rows || {};
                          const rowEntries = [
                            ["Floor", rows.floor],
                            ["Land Use", rows.landuse],
                            ["Land Area", rows.landarea],
                            ["Covered Area", rows.coveredarea],
                          ].filter(([, v]) => hasUsableValue(v));

                          if (isAction) {
                            return (
                              <div key={item.id} className="flex justify-center">
                                <div className="px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold">
                                  {actionText}
                                  {createdAt ? ` • ${createdAt}` : ""}
                                </div>
                              </div>
                            );
                          }

                          // Handle algorithm_response with custom header
                          if (isAlgorithmResponse) {
                            const stage = item.stage || "";
                            let agreementText = "";
                            if (stage.includes("(Agreed)")) agreementText = "(Agreed)";
                            else if (stage.includes("(Disagreed)")) agreementText = "(Disagreed)";
                            else if (stage.includes("(Partially Agreed)")) agreementText = "(Partially Agreed)";

                            return (
                              <div key={item.id} className="flex justify-end">
                                <div className="max-w-[85%] border rounded-2xl px-4 py-3 shadow-sm bg-blue-50 border-blue-200 text-blue-900">
                                  <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                                    <div className="text-xs font-semibold">
                                      Did inspector agree with Computer Vision Assessment? {agreementText}
                                    </div>
                                    {createdAt && <div className="text-[11px] opacity-80">{createdAt}</div>}
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          const sideClass = isSupervisor ? "justify-start" : "justify-end";
                          const bubbleClass = isSupervisor
                            ? "bg-gray-50 border-gray-200 text-gray-900"
                            : "bg-blue-50 border-blue-200 text-blue-900";

                          const headerLabel = isSupervisor ? "Supervisor comment" : isInspector ? "Inspector" : "Comment";

                          // Determine if reassessed or not reassessed
                          const isReassessed = selectedProperty?.reassess == 1 || selectedProperty?.reassess === "1";
                          const isInspectorExplanation = item.type === "inspector_explanation";

                          return (
                            <div key={item.id} className={`flex ${sideClass}`}>
                              <div className={`max-w-[85%] border rounded-2xl px-4 py-3 shadow-sm ${bubbleClass}`}>
                                <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                                  <div className="text-xs font-semibold">
                                    {isInspectorExplanation ? (
                                      // Custom header for inspector explanation
                                      <>
                                        inspector {item.user_name ? `${item.user_name} ` : ""}
                                        {isReassessed ? "reassessed the property" : "did not reassess the property"}
                                      </>
                                    ) : (
                                      // Default header for other types
                                      <>
                                        {headerLabel}
                                        {item.user_name ? ` • ${item.user_name}` : ""}
                                        {item.stage ? ` • ${item.stage}` : ""}
                                      </>
                                    )}
                                  </div>
                                  {createdAt && <div className="text-[11px] opacity-80">{createdAt}</div>}
                                </div>

                                {item.type === "inspector_explanation" ? (
                                  <div className="space-y-2">
                                    {hasUsableValue(item.overall) && (
                                      <div className="text-sm whitespace-pre-wrap">{item.overall}</div>
                                    )}
                                    {rowEntries.length > 0 && (
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {rowEntries.map(([k, v]) => (
                                          <div key={k} className="bg-white/70 border border-black/5 rounded-lg p-2">
                                            <div className="text-[11px] font-semibold opacity-80">{k}</div>
                                            <div className="text-sm whitespace-pre-wrap">{v}</div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-sm whitespace-pre-wrap">{item.text || ""}</div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              )}

            </MOTION.div>
          </MOTION.div>
        )}
      </AnimatePresence>

      {/* Fullscreen Image Modal */}
      {showFullImage && selectedProperty && getPropertyImage(selectedProperty) && !imageLoadErrors[selectedProperty.pin] && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4"
          onClick={() => {
            setShowFullImage(false);
            setRotation(0);
          }}
        >
          <div className="absolute top-6 right-6 flex space-x-4 z-10">
            <button
              onClick={handleRotate}
              className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all shadow-lg flex items-center justify-center"
              title="Rotate Image"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            {rotation !== 0 && (
              <button
                onClick={handleSaveRotation}
                disabled={savingImageRotation}
                className="p-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-full text-white transition-all shadow-lg flex items-center gap-2"
                title="Save Rotation"
              >
                {savingImageRotation ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </>
                )}
              </button>
            )}
            <button
              onClick={() => {
                setShowFullImage(false);
                setRotation(0);
              }}
              className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all shadow-lg flex items-center justify-center"
            >
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <MOTION.div
            initial={{ scale: 0.9, rotate: 0 }}
            animate={{ scale: 1, rotate: rotation }}
            exit={{ scale: 0.9 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={appendCacheBuster(resolveImageUrl(getPropertyImage(selectedProperty)), selectedProperty._imgBust)}
              className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg"
              alt="Full Property"
            />
          </MOTION.div>
        </div>
      )}
    </div>
  );
};

export default Property;